import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Trash2, Search, X, ChevronDown, ChevronUp, Mic, Type } from 'lucide-react';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import Button from '../Common/Button';
import ConfirmModal from '../Common/ConfirmModal';
import { apiService } from '../../services/apiService';
import { formatTimestamp, formatRelativeTime } from '../../utils/timezoneUtils';
import { useAuth } from '../../context/AuthContext';
import ConversationDrawer from './ConversationDrawer';

const ConversationsContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.dark.bg};
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0;
`;

const ConversationCount = styled.span`
  background: ${({ theme }) => theme.colors.primary[500]};
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const HeaderCenter = styled.div`
  flex: 1;
  max-width: 500px;
  margin: 0 ${({ theme }) => theme.spacing.xl};
`;

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  padding-left: 40px;
  background: ${({ theme }) => theme.colors.dark.surface};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  color: ${({ theme }) => theme.colors.dark.text};
  font-size: 0.875rem;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.dark.textSecondary};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const ClearSearchButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.dark.elevated};
    color: ${({ theme }) => theme.colors.dark.text};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.dark.bg};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.dark.border};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.dark.elevated};
  }
`;

const DateSection = styled(motion.div)`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const DateHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.dark.surface};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  user-select: none;

  &:hover {
    background: ${({ theme }) => theme.colors.dark.elevated};
    border-color: ${({ theme }) => theme.colors.primary[500]};
  }
`;

const DateHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const DateIcon = styled.div`
  width: 32px;
  height: 32px;
  background: ${({ theme }) => theme.colors.primary[500]}20;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary[400]};
  font-weight: 600;
  font-size: 0.875rem;
`;

const DateTitle = styled.h2`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0;
`;

const DateCount = styled.span`
  background: ${({ theme }) => theme.colors.primary[500]}20;
  color: ${({ theme }) => theme.colors.primary[400]};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.75rem;
  font-weight: 600;
`;

const DateHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const DateDeleteButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444; /* Bright red on hover */
    transform: scale(1.1);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ExpandIcon = styled.div`
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  display: flex;
  align-items: center;
  transition: transform ${({ theme }) => theme.transitions.fast};
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ConversationsList = styled(motion.div)`
  margin-top: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ConversationCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.dark.surface};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-left: 3px solid ${({ theme, mode }) => 
    mode === 'voice' ? theme.colors.accent.purple : theme.colors.primary[500]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.dark.elevated};
    border-color: ${({ theme }) => theme.colors.primary[500]};
    border-left-color: yellow;
    transform: translateX(2px);
  }
`;

const ConversationNumber = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  margin-right: ${({ theme }) => theme.spacing.xs};
  flex-shrink: 0;
`;

const ConversationContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const MessageRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  min-height: 24px;
`;

const MessageContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const MessageAuthor = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme, isUser }) => 
    isUser ? theme.colors.primary[400] : theme.colors.accent.purple};
  margin-right: ${({ theme }) => theme.spacing.xs};
  flex-shrink: 0;
`;

const MessageText = styled.div`
  font-size: 0.813rem;
  color: ${({ theme }) => theme.colors.dark.text};
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  
  /* Force single line preview - all elements inline */
  * {
    display: inline !important;
    margin: 0 !important;
    padding: 0 !important;
    white-space: nowrap;
  }
  
  /* Hide complex block elements completely */
  pre, ul, ol, blockquote, li {
    display: none !important;
  }
  
  /* Hide list markers and bullets */
  li::before, li::marker {
    display: none !important;
  }
  
  /* Markdown styling for inline elements only */
  p {
    display: inline !important;
    margin: 0 !important;
    
    &::after {
      content: ' ';
    }
  }
  
  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primary[400]};
  }
  
  em {
    font-style: italic;
  }
  
  code {
    background: ${({ theme }) => theme.colors.primary[500]}15;
    color: ${({ theme }) => theme.colors.primary[400]};
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 0.75rem;
    font-family: 'Courier New', monospace;
  }
  
  /* Hide headings in preview */
  h1, h2, h3, h4, h5, h6 {
    display: inline !important;
    font-size: inherit !important;
    font-weight: 600 !important;
  }
  
  /* Hide line breaks */
  br {
    display: none !important;
  }
`;

const MessageMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-shrink: 0;
  margin-left: auto;
`;

const MessageTime = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  white-space: nowrap;
`;

const ModeIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background: ${({ theme, mode }) => 
    mode === 'voice' ? `${theme.colors.accent.purple}20` : `${theme.colors.primary[500]}20`};
  color: ${({ theme, mode }) => 
    mode === 'voice' ? theme.colors.accent.purple : theme.colors.primary[400]};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.75rem;
  font-weight: 500;
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.status.error}20;
    color: #ef4444; /* Bright red on hover */
    transform: scale(1.1);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const LoadMoreButton = styled(motion.button)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.dark.surface};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  font-size: 0.875rem;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  margin-top: ${({ theme }) => theme.spacing.md};

  &:hover {
    background: ${({ theme }) => theme.colors.dark.elevated};
    border-color: ${({ theme }) => theme.colors.primary[500]};
    color: ${({ theme }) => theme.colors.primary[400]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  text-align: center;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  background: ${({ theme }) => theme.colors.dark.elevated};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  
  svg {
    width: 24px;
    height: 24px;
    color: ${({ theme }) => theme.colors.dark.textSecondary};
  }
`;

const SearchResultsHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.primary[500]}20;
  border: 1px solid ${({ theme }) => theme.colors.primary[500]}40;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  text-align: center;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.primary[400]};
  font-weight: 500;
`;

const CACHE_KEY = 'sarvik_conversations_cache';
const EXPANDED_STATE_KEY = 'sarvik_expanded_dates';
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes

const Conversations = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [groupedConversations, setGroupedConversations] = useState({});
  const [expandedDates, setExpandedDates] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  
  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    type: null, // 'conversation', 'date', 'all'
    data: null,
    loading: false
  });

  // Load expanded state from localStorage on mount
  useEffect(() => {
    const savedExpandedState = localStorage.getItem(EXPANDED_STATE_KEY);
    if (savedExpandedState) {
      try {
        const expandedArray = JSON.parse(savedExpandedState);
        setExpandedDates(new Set(expandedArray));
      } catch (e) {
        console.error('Failed to parse expanded state:', e);
        setExpandedDates(new Set()); // Default: all collapsed
      }
    }
  }, []);

  // Save expanded state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(EXPANDED_STATE_KEY, JSON.stringify([...expandedDates]));
  }, [expandedDates]);

  // Fetch conversations on mount (with caching)
  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    // Try to load from cache first
    const cached = loadFromCache();
    if (cached) {
      console.log('📦 Loaded conversations from cache');
      setConversations(cached.conversations);
      groupConversationsByDate(cached.conversations);
      setLastFetchTime(cached.timestamp);
      setLoading(false);
      
      // Fetch in background to update cache
      fetchConversations(true);
    } else {
      // No cache, fetch immediately
      fetchConversations(false);
    }
  };

  const loadFromCache = () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;
      
      const data = JSON.parse(cached);
      const age = Date.now() - data.timestamp;
      
      // Return cache if less than 5 minutes old
      if (age < CACHE_EXPIRY) {
        return data;
      }
      
      return null;
    } catch (e) {
      console.error('Failed to load cache:', e);
      return null;
    }
  };

  const saveToCache = (conversations) => {
    try {
      const data = {
        conversations,
        timestamp: Date.now()
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
      console.log('💾 Saved conversations to cache');
    } catch (e) {
      console.error('Failed to save cache:', e);
    }
  };

  const fetchConversations = async (isBackgroundUpdate = false) => {
    try {
      if (!isBackgroundUpdate) {
        setLoading(true);
      }
      
      // Backend limit is 100 max, so request 100 at a time
      const response = await apiService.getConversationHistory(100, 0);
      
      if (response.success) {
        setConversations(response.conversations);
        groupConversationsByDate(response.conversations);
        setLastFetchTime(Date.now());
        
        // Save to cache
        saveToCache(response.conversations);
        
        if (isBackgroundUpdate) {
          console.log('🔄 Updated conversations in background');
        }
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      if (!isBackgroundUpdate) {
        setLoading(false);
      }
    }
  };

  const groupConversationsByDate = (convs) => {
    const grouped = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    convs.forEach(conv => {
      const convDate = new Date(conv.timestamp_local || conv.timestamp);
      convDate.setHours(0, 0, 0, 0);
      
      let dateKey;
      let dateLabel;
      
      if (convDate.getTime() === today.getTime()) {
        dateKey = 'today';
        dateLabel = 'TODAY';
      } else if (convDate.getTime() === yesterday.getTime()) {
        dateKey = 'yesterday';
        dateLabel = 'YESTERDAY';
      } else {
        dateKey = convDate.toISOString().split('T')[0];
        dateLabel = convDate.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          year: 'numeric' 
        });
      }

      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          label: dateLabel,
          conversations: [],
          date: convDate
        };
      }

      grouped[dateKey].conversations.push(conv);
    });

    // Sort groups by date (newest first)
    // DO NOT reverse conversations here - keep ascending order for pairing
    const sortedGrouped = Object.keys(grouped)
      .sort((a, b) => grouped[b].date - grouped[a].date)
      .reduce((acc, key) => {
        acc[key] = grouped[key];
        return acc;
      }, {});

    setGroupedConversations(sortedGrouped);
  };

  const toggleDateExpansion = (dateKey) => {
    setExpandedDates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dateKey)) {
        newSet.delete(dateKey);
      } else {
        newSet.add(dateKey);
      }
      return newSet;
    });
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      groupConversationsByDate(conversations);
      return;
    }

    const lowerQuery = query.toLowerCase();
    
    // Filter conversations: search in BOTH user query and assistant response
    const filtered = [];
    for (let i = 0; i < conversations.length; i++) {
      const conv = conversations[i];
      
      // If it's a user message
      if (conv.role === 'user') {
        const userText = conv.text.toLowerCase();
        const nextConv = conversations[i + 1];
        const assistantText = (nextConv && nextConv.role === 'assistant') 
          ? nextConv.text.toLowerCase() 
          : '';
        
        // Check if query exists in user message OR assistant response
        if (userText.includes(lowerQuery) || assistantText.includes(lowerQuery)) {
          filtered.push(conv);
          // Also include the assistant response if it exists
          if (nextConv && nextConv.role === 'assistant') {
            filtered.push(nextConv);
            i++; // Skip next iteration since we already added it
          }
        }
      }
    }
    
    groupConversationsByDate(filtered);
    
    // Auto-expand sections with results
    const matchingDates = new Set();
    Object.keys(groupedConversations).forEach(dateKey => {
      if (groupedConversations[dateKey].conversations.length > 0) {
        matchingDates.add(dateKey);
      }
    });
    setExpandedDates(matchingDates);
  };

  const handleClearAll = () => {
    setDeleteModal({
      isOpen: true,
      type: 'all',
      data: null,
      loading: false
    });
  };

  const handleDeleteDate = (dateKey, e) => {
    e.stopPropagation();
    setDeleteModal({
      isOpen: true,
      type: 'date',
      data: dateKey,
      loading: false
    });
  };

  const handleDeleteConversation = (conversationId, e) => {
    e.stopPropagation();
    setDeleteModal({
      isOpen: true,
      type: 'conversation',
      data: conversationId,
      loading: false
    });
  };

  const confirmDelete = async () => {
    setDeleteModal(prev => ({ ...prev, loading: true }));
    
    try {
      if (deleteModal.type === 'conversation') {
        // Delete single conversation (user + assistant pair)
        await apiService.deleteConversation(deleteModal.data);
        
        // Remove from state - remove both user and assistant messages
        const updated = conversations.filter(conv => {
          // Keep conversations that don't match the ID
          // Also remove the assistant response if this is a user message being deleted
          if (conv.id === deleteModal.data) return false;
          // Check if previous message was deleted (remove orphaned assistant)
          const convIndex = conversations.findIndex(c => c.id === conv.id);
          if (convIndex > 0 && conversations[convIndex - 1].id === deleteModal.data) {
            return false;
          }
          return true;
        });
        
        setConversations(updated);
        groupConversationsByDate(updated);
        saveToCache(updated); // Update cache
        
      } else if (deleteModal.type === 'date') {
        // Delete all conversations for a specific date
        const dateGroup = groupedConversations[deleteModal.data];
        if (dateGroup) {
          // Delete each conversation in this date group
          const conversationIds = dateGroup.conversations
            .filter(c => c.role === 'user')
            .map(c => c.id);
          
          await Promise.all(
            conversationIds.map(id => apiService.deleteConversation(id))
          );
          
          // Remove from state
          const idsToRemove = new Set(dateGroup.conversations.map(c => c.id));
          const updated = conversations.filter(conv => !idsToRemove.has(conv.id));
          
          setConversations(updated);
          groupConversationsByDate(updated);
          saveToCache(updated); // Update cache
        }
        
      } else if (deleteModal.type === 'all') {
        // Delete all conversations
        await apiService.clearConversationHistory();
        setConversations([]);
        setGroupedConversations({});
        localStorage.removeItem(CACHE_KEY); // Clear cache
      }
      
      // Close modal
      setDeleteModal({ isOpen: false, type: null, data: null, loading: false });
      
    } catch (error) {
      console.error('Failed to delete:', error);
      setDeleteModal(prev => ({ ...prev, loading: false }));
      toast.error('Failed to delete. Please try again.', {
        duration: 5000,
      });
    }
  };

  const handleConversationClick = (conversation) => {
    setSelectedConversation(conversation);
    setShowDrawer(true);
  };

  // Calculate stats for display
  const totalDays = Object.keys(groupedConversations).length;
  const totalChats = conversations.filter(c => c.role === 'user').length; // Count only user messages (each represents one chat)
  
  const totalConversations = conversations.length;
  const filteredCount = Object.values(groupedConversations).reduce(
    (sum, group) => sum + group.conversations.length, 
    0
  );

  // Group conversations into pairs (user + assistant)
  // Uses conversation_group_id if available (robust), falls back to position (legacy)
  const groupConversationsIntoPairs = (convs) => {
    const pairs = [];
    
    // Check if we have conversation_group_id (new robust method)
    const hasGroupIds = convs.some(c => c.conversation_group_id);
    
    if (hasGroupIds) {
      // ROBUST METHOD: Group by conversation_group_id
      const grouped = {};
      convs.forEach(conv => {
        const groupId = conv.conversation_group_id || conv.id;
        if (!grouped[groupId]) grouped[groupId] = {};
        grouped[groupId][conv.role] = conv;
      });
      
      // Convert to pairs
      Object.values(grouped).forEach(group => {
        if (group.user) {
          pairs.push({
            user: group.user,
            assistant: group.assistant || null,
            id: group.user.id
          });
        }
      });
    } else {
      // LEGACY METHOD: Position-based pairing (fallback)
      for (let i = 0; i < convs.length; i++) {
        if (convs[i].role === 'user') {
          const assistant = convs[i + 1];
          if (assistant && assistant.role === 'assistant') {
            pairs.push({
              user: convs[i],
              assistant: convs[i + 1],
              id: convs[i].id
            });
            i++; // Skip the assistant message
          } else {
            // User message without response
            pairs.push({
              user: convs[i],
              assistant: null,
              id: convs[i].id
            });
          }
        }
      }
    }
    
    return pairs;
  };

  return (
    <ConversationsContainer>
      <Header>
        <HeaderLeft>
          <Title>Conversations</Title>
          {totalChats > 0 && (
            <ConversationCount>
              {totalDays} {totalDays === 1 ? 'Day' : 'Days'} • {totalChats} {totalChats === 1 ? 'Chat' : 'Chats'}
            </ConversationCount>
          )}
        </HeaderLeft>

        <HeaderCenter>
          <SearchContainer>
            <SearchIcon>
              <Search />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search conversations by keyword or date..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
            {searchQuery && (
              <ClearSearchButton onClick={() => handleSearch('')}>
                <X />
              </ClearSearchButton>
            )}
          </SearchContainer>
        </HeaderCenter>

        <HeaderActions>
          {totalConversations > 0 && (
            <Button
              variant="danger"
              size="sm"
              onClick={handleClearAll}
            >
              <Trash2 />
              Clear All
            </Button>
          )}
        </HeaderActions>
      </Header>

      <ContentArea>
        {loading ? (
          <EmptyState>
            <EmptyIcon>
              <MessageSquare />
            </EmptyIcon>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '1rem' }}>Loading conversations...</h3>
          </EmptyState>
        ) : totalConversations === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <MessageSquare />
            </EmptyIcon>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '1rem' }}>No Conversations Yet</h3>
            <p style={{ margin: 0, fontSize: '0.875rem' }}>
              Start chatting with SARVIK to see your conversation history here.
            </p>
          </EmptyState>
        ) : (
          <>
            {searchQuery && (
              <SearchResultsHeader>
                Found {filteredCount} result{filteredCount !== 1 ? 's' : ''} for "{searchQuery}"
              </SearchResultsHeader>
            )}

            {Object.keys(groupedConversations).map((dateKey) => {
              const group = groupedConversations[dateKey];
              const isExpanded = expandedDates.has(dateKey);
              // Pair in ascending order, then reverse pairs to show newest first
              const pairs = groupConversationsIntoPairs(group.conversations).reverse();

              return (
                <DateSection
                  key={dateKey}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <DateHeader onClick={() => toggleDateExpansion(dateKey)}>
                    <DateHeaderLeft>
                      <DateTitle>{group.label}</DateTitle>
                      <DateCount>{pairs.length} chat{pairs.length !== 1 ? 's' : ''}</DateCount>
                    </DateHeaderLeft>
                    
                    <DateHeaderRight>
                      <DateDeleteButton
                        onClick={(e) => handleDeleteDate(dateKey, e)}
                      >
                        <Trash2 />
                      </DateDeleteButton>
                      
                      <ExpandIcon>
                        {isExpanded ? <ChevronUp /> : <ChevronDown />}
                      </ExpandIcon>
                    </DateHeaderRight>
                  </DateHeader>

                  <AnimatePresence>
                    {isExpanded && (
                      <ConversationsList
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        {pairs.map((pair, index) => (
                          <ConversationCard
                            key={pair.id}
                            mode={pair.user.mode}
                            onClick={() => handleConversationClick(pair)}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.2, delay: index * 0.05 }}
                          >
                            <ConversationContent>
                              {/* User Message - Single Line */}
                              <MessageRow>
                                <MessageContent style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                                  <ConversationNumber>#{index + 1}</ConversationNumber>
                                  <MessageAuthor isUser={true}> User:</MessageAuthor>
                                  <MessageText>
                                    <ReactMarkdown>{pair.user.text}</ReactMarkdown>
                                  </MessageText>
                                </MessageContent>
                                
                                <MessageMeta>
                                  <MessageTime>
                                    {pair.user.timestamp_local 
                                      ? new Date(pair.user.timestamp_local).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
                                      : formatTimestamp(pair.user.timestamp, user?.timezone || 'UTC', { timeOnly: true })
                                    }
                                  </MessageTime>
                                  <ModeIndicator mode={pair.user.mode}>
                                    {pair.user.mode === 'voice' ? <Mic /> : <Type />}
                                  </ModeIndicator>
                                  <DeleteButton onClick={(e) => handleDeleteConversation(pair.id, e)}>
                                    <Trash2 />
                                  </DeleteButton>
                                </MessageMeta>
                              </MessageRow>

                              {/* Assistant Response - Single Line */}
                              {pair.assistant && (
                                <MessageRow>
                                  <MessageContent style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0, paddingLeft: '22px' }}>
                                    <MessageAuthor isUser={false}>SARVIK:</MessageAuthor>
                                    <MessageText>
                                      <ReactMarkdown>{pair.assistant.text}</ReactMarkdown>
                                    </MessageText>
                                  </MessageContent>
                                </MessageRow>
                              )}
                            </ConversationContent>
                          </ConversationCard>
                        ))}
                      </ConversationsList>
                    )}
                  </AnimatePresence>
                </DateSection>
              );
            })}
          </>
        )}
      </ContentArea>

      {/* Drawer for full conversation view */}
      <AnimatePresence>
        {showDrawer && selectedConversation && (
          <ConversationDrawer
            conversation={selectedConversation}
            onClose={() => {
              setShowDrawer(false);
              setSelectedConversation(null);
            }}
            onDelete={(deletedId) => {
              // Refresh conversations list after delete
              fetchConversations();
            }}
          />
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, type: null, data: null, loading: false })}
        onConfirm={confirmDelete}
        title={
          deleteModal.type === 'all' ? 'Delete All Conversations' :
          deleteModal.type === 'date' ? `Delete ${groupedConversations[deleteModal.data]?.label} Conversations` :
          'Delete Conversation'
        }
        subtitle={
          deleteModal.type === 'all' ? `${totalChats} ${totalChats === 1 ? 'chat' : 'chats'}` :
          deleteModal.type === 'date' ? `${groupedConversations[deleteModal.data]?.conversations.filter(c => c.role === 'user').length || 0} chats` :
          'This action cannot be undone'
        }
        message={
          deleteModal.type === 'all' ? 
            'Are you sure you want to delete all your conversations? This will permanently remove all chat history from both your device and the cloud.' :
          deleteModal.type === 'date' ?
            `This will permanently delete all conversations from ${groupedConversations[deleteModal.data]?.label}. This action cannot be undone.` :
            'This conversation and its response will be permanently deleted from both your device and the cloud.'
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        icon={Trash2}
        loading={deleteModal.loading}
        loadingText={deleteModal.type === 'all' ? 'Deleting All...' : 'Deleting...'}
      />
    </ConversationsContainer>
  );
};

export default Conversations;
